import React, { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import useRequestJSON from '../../libs/hooks/useRequestJSON';
import { UserResponse, AuthProviderValue } from './types';
import { PostJSON } from '../../libs/request';

const AuthContext = React.createContext<AuthProviderValue>({
  user: null,
  isAdmin: false,
  isLightbeam: false,
  isLoading: true,
  error: null,
  updateUser: null
});

export const transformUserResponse = (
  response: UserResponse
): UserResponse['user'] => {
  return response.user;
};

/**
 * Checks whether or not a user is logged in. Stores
 * the user in its state if the user is logged in,
 * and provides a way to update that user state.
 */
export const AuthProvider: React.FC = (props) => {
  // useCallback here ensures that this request is only called once
  const request = useCallback(
    () => PostJSON<UserResponse>({ url: '/user/logged_in/' }),
    []
  );
  const {
    data: user,
    setData,
    isLoading,
    error
  } = useRequestJSON<UserResponse, UserResponse['user']>({
    request,
    transformFn: transformUserResponse
  });

  // Add to Sentry context
  Sentry.setUser({
    id: user ? String(user.id) : null,
    email: user ? user.email : null
  });

  const value: AuthProviderValue = {
    user,
    isAdmin: user ? user.isAdmin : false,
    isLightbeam: user ? user.account?.isLightbeam : false,
    isLoading,
    error,
    updateUser(updatedUser) {
      setData(updatedUser);
    }
  };

  if (isLoading) {
    return null;
  }

  return <AuthContext.Provider value={value} {...props} />;
};

export function useAuth(): AuthProviderValue {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
